import { useMutation } from '@apollo/client/react';
import { Button, Descriptions, Tag, Upload, message } from 'antd';
import clsx from 'clsx';
import { includes } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../AppContext';
import alternateLogo from '../../assets/images/alternateLogo.png';
import alternateProfile from '../../assets/images/alternateProfile.png';
import { DeleteButton, EditButton, EditIcon, InfoIcon } from '../../assets/svg';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import useQueryWithCallbacks from '../../common/CommonUseQuery';
import {
  ALLOWED_ACTION_KEYS,
  BREAKPOINTS,
  DEFAULTDATEFORMAT,
  FILE_SIZE_20MB,
  GA_EVENT,
  GA_LABEL,
  SYSTEM_ROLES,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import { compressImage, fileUpload, titleCase } from '../../common/utils';
import CanPerform from '../../components/CanPerform';
import CommonCard from '../../components/CommonCard';
import CommonPopover from '../../components/CommonPopover';
import { GET_PROFILE } from '../users/graphql/Queries';
import CropperModal from './CropperModal';
import EditModal from './EditModal';
import { UPDATE_LOGGED_IN_USER, UPDATE_TENANT_LOGO } from './graphql/Mutation';
import { GET_URL } from './graphql/Queries';

const Profile = () => {
  const { getCurrentUser, dispatch, initializeAuth } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const [showModal, setShowModal] = useState(false);
  const [showCropperModal, setShowCropperModal] = useState(false);
  const [filePrefix, setFilePrefix] = useState();
  const [fileObj, setFileObj] = useState();
  const [fileData, setFileData] = useState();
  const [src, setSrc] = useState();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isMobileViewport = useMedia(`(max-width: ${BREAKPOINTS.mobile}px)`);
  const [showImageSizeError, setShowImageSizeError] = useState(false);
  const [
    showImageSizeErrorForOrganization,
    setShowImageSizeErrorForOrganization,
  ] = useState(false);

  const {
    data,
    loading,
    refetch: refetchProfile,
  } = useQueryWithCallbacks(GET_PROFILE, {
    fetchPolicy: 'network-only',
  });

  const refetch = () => {
    refetchProfile().then((res) => {
      initializeAuth('', '', res?.data?.getLoggedInUser);
    });
  };

  useEffect(() => {
    if (currentUser?.emailVerified || currentUser?.numberVerified) {
      refetch();
    }
  }, [currentUser?.emailVerified, currentUser?.numberVerified]);

  const [updateImage] = useMutation(UPDATE_LOGGED_IN_USER, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.EDIT_USER_LOGO, {
        label: GA_LABEL.EDIT_USER_LOGO,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      refetch();
    },
  });
  const [updateTenantLogo] = useMutation(UPDATE_TENANT_LOGO, {
    onError() {},
    onCompleted() {
      Event(GA_EVENT.EDIT_TENANT_LOGO, {
        label: GA_LABEL.EDIT_TENANT_LOGO,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        user_id: currentUser?.id,
        user_name: currentUser?.name,
        tenant_id: currentUser?.tenantUser?.tenant?.id,
        tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
      });
      refetch();
    },
  });

  const [fetchSignedUrl] = useLazyQueryWithCallbacks(GET_URL, {
    onCompleted: (res) => {
      const signedImageUrl = res?.getSignedPutUrl?.signedUrl;
      fileUpload(signedImageUrl, fileObj)
        .then(() => {
          updateImage({
            variables: {
              data: {
                profileImage: res?.getSignedPutUrl?.key,
              },
            },
          });
        })
        .catch(() => {
          message.error('Error while uploading image');
        });
    },
    onError: () => {},
  });

  const handleModal = () => {
    setShowModal(true);
  };
  const readFile = (fileObject) => {
    // eslint-disable-next-line no-undef
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setSrc(reader.result);
    });
    reader.readAsDataURL(fileObject);
  };

  const handleUploadprofileImage = (info) => {
    try {
      fetchSignedUrl({
        variables: {
          data: {
            fileName: `profile-image/${info?.name}`,
            contentType: info?.type,
            acl: 'private',
          },
        },
      });
      setFileObj(info);
    } catch (error) {
      message.error('Error while uploading image');
      return error;
    }
  };
  const profileImageProps = {
    accept: '.png,.jpg,.jpeg',
    onChange: (info) => {
      const file = info?.file;
      setShowImageSizeError(false);
      if (file?.size > FILE_SIZE_20MB) {
        setShowImageSizeError(true);
        return false;
      }
      compressImage({
        file,
        fileType: file?.type?.split('/')?.[1],
        callBack: handleUploadprofileImage,
      });
    },
    beforeUpload: () => {
      return false;
    },
  };

  const logoProps = {
    accept: '.png,.jpg,.jpeg',
    onChange: (info) => {
      const file = info?.file;
      setShowImageSizeErrorForOrganization(false);
      if (file?.size > FILE_SIZE_20MB) {
        setShowImageSizeErrorForOrganization(true);
        return false;
      }
      setFilePrefix('tenant-image');
      const callBack = (compressedImage) => {
        setFileData(compressedImage);
        readFile(compressedImage);
      };
      compressImage({
        file,
        fileType: file?.type?.split('/')?.[1],
        callBack,
      });
      setShowCropperModal(true);
    },
    beforeUpload: () => {
      return false;
    },
  };
  const removeProfilePicture = () => {
    updateImage({
      variables: {
        data: {
          profileImage: null,
        },
      },
    });
  };
  const removeOrganisationProfilePicture = () => {
    updateTenantLogo({
      variables: {
        logo: null,
      },
    });
  };

  const getPlanInfo = (name) => {
    return (
      <CommonPopover
        id="plan-info-popover"
        destroyOnHidden
        getPopupContainer={() =>
          // eslint-disable-next-line no-undef
          document.querySelector('.profile-content')
        }
        placement="bottom"
        classNames={{ root: 'plan-info-content' }}
        content={
          <div
            id="plan-info-popover-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h5 id="plan-info-title">digiQC {name} plan</h5>
          </div>
        }
      >
        <InfoIcon
          id="plan-info-icon"
          height={16}
          width={16}
          className="info-icon ml-5"
          onClick={(e) => e.stopPropagation()}
        />
      </CommonPopover>
    );
  };

  return (
    <>
      {showModal && (
        <EditModal
          showModal={showModal}
          setShowModal={setShowModal}
          userData={data?.getLoggedInUser}
          refetchUserData={refetch}
        />
      )}
      {showCropperModal && (
        <CropperModal
          showModal={showCropperModal}
          setShowModal={setShowCropperModal}
          filePrefix={filePrefix}
          fileData={fileData}
          refetch={refetch}
          src={src}
          setSrc={setSrc}
          isTenantLogo
        />
      )}
      {!isDesktopViewport && (
        <div
          id="profile-mobile-header"
          className="profile-header d-flex justify-between"
        >
          <div
            id="profile-mobile-page-title"
            className="page-header text-primary"
          >
            Personal Details
          </div>
          <Button
            id="profile-mobile-edit-button"
            shape="round"
            type="primary"
            icon={<EditIcon />}
            className="d-flex pointer"
            onClick={() => {
              handleModal();
            }}
          />
        </div>
      )}
      <div id="profile-mobile-card-wrapper" className="mobile-card-wrapper">
        <CommonCard
          id="profile-personal-details-card"
          className="profile"
          loading={loading}
        >
          <div id="profile-personal-details" className="personal-details">
            {isDesktopViewport && (
              <div
                id="profile-desktop-header"
                className="profile-header d-flex justify-between"
              >
                <h2 id="profile-desktop-title">Personal Details</h2>
                <Button
                  id="profile-desktop-edit-button"
                  shape="round"
                  type="primary"
                  icon={<EditIcon />}
                  className="d-flex pointer"
                  onClick={() => {
                    handleModal();
                  }}
                >
                  Edit
                </Button>
              </div>
            )}
            <div id="profile-content" className="profile-content d-flex">
              <div id="profile-content-image" className="profile-content-image">
                <img
                  id="profile-image"
                  alt="Profile"
                  src={data?.getLoggedInUser?.profileImage || alternateProfile}
                  className="profile-image"
                />
                <div id="profile-image-actions" className="action-btns d-flex">
                  <Upload
                    id="profile-image-upload"
                    showUploadList={false}
                    {...profileImageProps}
                  >
                    <EditButton
                      id="profile-image-edit-btn"
                      className="edit-btn pointer mr-5"
                    />
                  </Upload>
                  {data?.getLoggedInUser?.profileImage && (
                    <DeleteButton
                      id="profile-image-delete-btn"
                      className="delete-btn pointer"
                      onClick={removeProfilePicture}
                    />
                  )}
                </div>
                {showImageSizeError && !isDesktopViewport && (
                  <div
                    id="profile-image-size-error-mobile"
                    className="text-danger font-size-12 mt-10"
                  >
                    * Maximum file size is 20MB
                  </div>
                )}
              </div>
              <Descriptions
                id="profile-descriptions"
                layout="vertical"
                column={isDesktopViewport ? 2 : 1}
              >
                <Descriptions.Item id="profile-name-item" label="Name">
                  {data?.getLoggedInUser?.name}
                </Descriptions.Item>
                <Descriptions.Item id="profile-email-item" label="Email">
                  <div
                    id="profile-email-content"
                    className={clsx(
                      'd-flex align-center',
                      isMobileViewport && 'flex-vertical',
                    )}
                  >
                    {data?.getLoggedInUser?.email}
                    {data?.getLoggedInUser?.email &&
                      !data?.getLoggedInUser?.emailVerified && (
                        <>
                          <Tag
                            id="profile-email-unverified-tag"
                            className="yellow-tag ml-5"
                          >
                            Unverified
                          </Tag>
                          <Button
                            id="profile-email-verify-button"
                            type="link"
                            className="d-flex pointer p-0 text-underline"
                            onClick={() => {
                              dispatch({
                                type: 'SET_SHOW_VERIFY_DETAILS_MODAL',
                                data: true,
                              });
                            }}
                            size={26}
                          >
                            Verify
                          </Button>
                        </>
                      )}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item id="profile-role-item" label="Role">
                  {titleCase(data?.getLoggedInUser?.roles[0])}
                </Descriptions.Item>
                <Descriptions.Item id="profile-phone-item" label="Phone Number">
                  <div
                    id="profile-phone-content"
                    className={clsx(
                      'd-flex align-center',
                      isMobileViewport && 'flex-vertical',
                    )}
                  >
                    {data?.getLoggedInUser?.phoneNo}
                    {data?.getLoggedInUser?.phoneNo &&
                      !data?.getLoggedInUser?.numberVerified && (
                        <>
                          <Tag
                            id="profile-phone-unverified-tag"
                            className="yellow-tag ml-5"
                          >
                            Unverified
                          </Tag>
                          <Button
                            id="profile-phone-verify-button"
                            type="link"
                            className="d-flex pointer p-0 text-underline"
                            onClick={() => {
                              dispatch({
                                type: 'SET_SHOW_VERIFY_DETAILS_MODAL',
                                data: true,
                              });
                            }}
                            size={26}
                          >
                            Verify
                          </Button>
                        </>
                      )}
                  </div>
                </Descriptions.Item>
              </Descriptions>
            </div>
            {showImageSizeError && isDesktopViewport && (
              <div
                id="profile-image-size-error-desktop"
                className="text-danger font-size-12"
              >
                * Maximum file size is 20MB
              </div>
            )}
          </div>
        </CommonCard>
      </div>
      {includes(data?.getLoggedInUser?.roles, SYSTEM_ROLES.SYSTEM_ADMIN) && (
        <>
          {!isDesktopViewport && (
            <div
              id="org-mobile-header"
              className="profile-header d-flex justify-between"
            >
              <div
                id="org-mobile-page-title"
                className="page-header text-primary"
              >
                Organization Details
              </div>
            </div>
          )}
          <div id="org-mobile-card-wrapper" className="mobile-card-wrapper">
            <CommonCard
              id="org-details-card"
              className="profile"
              loading={loading}
            >
              <div id="org-details" className="organization-details">
                {isDesktopViewport && (
                  <div
                    id="org-desktop-header"
                    className="d-flex justify-between mb-10"
                  >
                    <h2 id="org-desktop-title">Organization Details</h2>
                  </div>
                )}

                <div id="org-content" className="profile-content d-flex">
                  <div id="org-content-image" className="profile-content-image">
                    <img
                      id="org-logo-image"
                      alt="Logo"
                      src={
                        data?.getLoggedInUser?.tenantUser?.tenant?.logo ||
                        alternateLogo
                      }
                      className="profile-image"
                    />
                    <div id="org-logo-actions" className="action-btns d-flex">
                      <CanPerform action={ALLOWED_ACTION_KEYS.EDIT_ORG_LOGO}>
                        <Upload
                          id="org-logo-upload"
                          showUploadList={false}
                          {...logoProps}
                        >
                          <EditButton
                            id="org-logo-edit-btn"
                            className="edit-btn pointer  mr-5"
                          />
                        </Upload>
                        {data?.getLoggedInUser?.tenantUser?.tenant?.logo && (
                          <DeleteButton
                            id="org-logo-delete-btn"
                            className="delete-btn pointer"
                            onClick={removeOrganisationProfilePicture}
                          />
                        )}
                      </CanPerform>
                    </div>
                    {showImageSizeErrorForOrganization &&
                      !isDesktopViewport && (
                        <div
                          id="org-image-size-error-mobile"
                          className="text-danger font-size-12 mt-10"
                        >
                          * Maximum file size is 20MB
                        </div>
                      )}
                  </div>
                  <Descriptions
                    id="org-descriptions"
                    layout="vertical"
                    column={isDesktopViewport ? 2 : 1}
                  >
                    <Descriptions.Item id="org-name-item" label="Name">
                      {
                        data?.getLoggedInUser?.tenantUser?.tenant
                          ?.organizationName
                      }
                    </Descriptions.Item>
                    <Descriptions.Item
                      id="org-plan-item"
                      label={<div className="d-flex align-center">Plan</div>}
                    >
                      <div
                        id="org-plan-content"
                        className="d-flex align-center"
                      >
                        {
                          data?.getLoggedInUser?.tenantUser?.tenant
                            ?.subscription?.planObj?.name
                        }
                        {getPlanInfo(
                          data?.getLoggedInUser?.tenantUser?.tenant
                            ?.subscription?.planObj?.name,
                        )}
                      </div>
                    </Descriptions.Item>

                    <Descriptions.Item
                      id="org-user-limit-item"
                      label="User Limit"
                    >
                      {data?.getLoggedInUser?.tenantUser?.tenant?.userLimit}
                    </Descriptions.Item>
                    <Descriptions.Item
                      id="org-valid-till-item"
                      label="Valid Till"
                    >
                      {moment(
                        `${data?.getLoggedInUser?.tenantUser?.tenant?.subscription?.validTill}`,
                      ).format(DEFAULTDATEFORMAT)}
                    </Descriptions.Item>
                    <Descriptions.Item id="org-timezone-item" label="TimeZone">
                      {data?.getLoggedInUser?.tenantUser?.tenant?.timeZone ||
                        '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
                {showImageSizeErrorForOrganization && isDesktopViewport && (
                  <div
                    id="org-image-size-error-desktop"
                    className="text-danger font-size-12"
                  >
                    * Maximum file size is 20MB
                  </div>
                )}
              </div>
            </CommonCard>
          </div>
        </>
      )}
    </>
  );
};

export default Profile;
